import React, { useState } from 'react';
import { parseInlineMarkdown, renderPlainTextWithFormatting } from '../../../../../../utils/LearnTab/slideTextRenderer';
import { tUi } from '../../../../../../../../../utils/dq_lc';
import {
  HtmlContentLayout,
  HtmlImageContainer,
  HtmlContentColumn,
  HtmlContentWrapper,
  ImagePlaceholder,
  SlideTitle,
} from '../../../../../../styles/LearnTab/slideBase.styles';
import { SlideImageLightboxModal } from '../../common/SlideImageLightbox.comp';
import {
  CaseSectionStack,
  CaseSectionGrid,
  CaseSection,
  CaseSectionLabel,
  CaseChoice,
  CaseChoiceBadge,
} from './CaseStudyCard.styles';

const textOf = (value) => (typeof value === 'string' ? value.trim() : '');
const listOf = (value) => (Array.isArray(value) ? value.map(textOf).filter(Boolean) : []);
const prose = (value) => {
  const text = textOf(value);
  return text ? renderPlainTextWithFormatting(text) : null;
};

export const CaseStudyCard = ({
  slide,
  slideEdits = {},
  showEnglishContent = false,
  highlightWord,
  onImageClick,
  uiLocale = 'en-US',
}) => {
  const [internalLightbox, setInternalLightbox] = useState(null);
  const origin = slide?._pbdq_origin || slide || {};

  const rawTitle = origin.case_title || slide?.SlideTitle || origin.title || origin.headline || '';
  const titleEdit = slideEdits?.['title'];
  const baseTitle = showEnglishContent ? (titleEdit?.original || rawTitle) : (titleEdit?.edited || rawTitle);
  const displayTitle = baseTitle;
  const snippet = origin.case_snippet || origin.body || slide?.SlideContent || '';
  const isIllustrative = origin.is_illustrative;
  const part = Number(origin.case_part);
  // Contract: part 2 never shows an image, even if one is attached.
  const showImage = Boolean(slide?.Images?.[0]) && part !== 2;

  const handleImgClick = (img) => {
    if (onImageClick) {
      onImageClick(img);
    } else {
      setInternalLightbox(img);
    }
  };

  const name = textOf(origin.character_name);
  const role = textOf(origin.character_role);
  const goal = textOf(origin.character_goal);
  const choiceQuestion = textOf(origin.choice_question);
  const choices = listOf(origin.choices);
  const risks = listOf(origin.risks);
  const questions = listOf(origin.discussion_questions);

  // [key, content] in display order; key is also the i18n suffix and the data-case-section value.
  const sections = part === 1 ? [
    ['story', prose(origin.story)],
    ['meet', (name || role || goal) && (
      <>
        {(name || role) && (
          <div>
            {name && <strong>{name}</strong>}
            {name && role && ' — '}
            {role && parseInlineMarkdown(role)}
          </div>
        )}
        {goal && <div>{parseInlineMarkdown(goal)}</div>}
      </>
    )],
    ['problem', prose(origin.problem)],
    ['choice', (choiceQuestion || choices.length > 0) && (
      <>
        {choiceQuestion && <div>{parseInlineMarkdown(choiceQuestion)}</div>}
        {choices.map((choice, idx) => (
          <CaseChoice key={idx} data-choice>
            <CaseChoiceBadge data-choice-letter>{String.fromCharCode(65 + idx)}</CaseChoiceBadge>
            <span>{parseInlineMarkdown(choice)}</span>
          </CaseChoice>
        ))}
      </>
    )],
    ['stopAndThink', prose(origin.stop_and_think)],
  ] : part === 2 ? [
    ['risks', risks.length > 0 && (
      <ul>{risks.map((risk, idx) => <li key={idx}>{parseInlineMarkdown(risk)}</li>)}</ul>
    )],
    ['whatHappened', prose(origin.what_happened)],
    ['bigIdea', prose(origin.big_idea)],
    ['itsLike', prose(origin.everyday_comparison)],
    ['letsTalk', questions.length > 0 && (
      <ol>{questions.map((question, idx) => <li key={idx}>{parseInlineMarkdown(question)}</li>)}</ol>
    )],
  ] : [];
  const filledSections = sections.filter(([, content]) => content);
  const Sections = part === 2 ? CaseSectionGrid : CaseSectionStack;
  // Part 2 grid: LET'S TALK takes a full row, and so does a card that would otherwise sit alone
  // (e.g. THE BIG IDEA when there is no IT'S LIKE…).
  const paired = filledSections.filter(([key]) => key !== 'letsTalk').length;
  const isWide = (key, idx) => part === 2 && (key === 'letsTalk' || (paired % 2 === 1 && idx === paired - 1));

  const content = (
    <HtmlContentColumn>
      {displayTitle && (
        <SlideTitle>
          {highlightWord ? highlightWord('title', displayTitle) : displayTitle}
        </SlideTitle>
      )}
      <HtmlContentWrapper>
        {isIllustrative && (
          <div className="illustrative-badge" style={{ marginBottom: 12 }}>
            {tUi('slide.content.illustrativeExample', uiLocale)}
          </div>
        )}
        {filledSections.length > 0 ? (
          <Sections>
            {filledSections.map(([key, body], idx) => (
              <CaseSection key={key} data-case-section={key} data-wide={isWide(key, idx) || undefined}>
                <CaseSectionLabel data-case-label>{tUi(`slide.caseStudy.${key}`, uiLocale)}</CaseSectionLabel>
                {body}
              </CaseSection>
            ))}
          </Sections>
        ) : snippet && (
          <div className="content-body" style={{ textAlign: 'left', width: '100%' }}>
            {renderPlainTextWithFormatting(snippet)}
          </div>
        )}
      </HtmlContentWrapper>
    </HtmlContentColumn>
  );

  return (
    <>
      {showImage ? (
        <HtmlContentLayout>
          <HtmlImageContainer>
            <ImagePlaceholder>
              <img
                src={slide.Images[0]}
                alt="Slide image"
                onClick={() => handleImgClick(slide.Images[0])}
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  WebkitUserDrag: 'none',
                }}
              />
            </ImagePlaceholder>
          </HtmlImageContainer>
          {content}
        </HtmlContentLayout>
      ) : content}

      {!onImageClick && (
        <SlideImageLightboxModal
          image={internalLightbox}
          onClose={() => setInternalLightbox(null)}
        />
      )}
    </>
  );
};

export default CaseStudyCard;
